import {useEffect} from 'react';
import {toast} from 'react-toastify';
import {SYSTEM_ANNOUNCEMENTS_CONTAINER_ID} from './constants';

// Spacing between toasts in react-toastify's expanded stack (0.3s transition).
const STACK_GAP = 12;
const STACK_TRANSITION_MS = 350;

export const SYSTEM_ANNOUNCEMENTS_OFFSET_VAR = '--system-announcements-offset';

/**
 * Keeps the CSS variable `--system-announcements-offset` in sync with the
 * visual height of the (stacked) system-announcements toast container,
 * tracking hover expansion so the transient feedback toasts below never get
 * overlapped.
 *
 * Deliberately NOT React state: re-rendering a mounted ToastContainer with a
 * changed `style` prop leaves its toasts with a stale close handler in
 * react-toastify v11 (close button clicks are swallowed until the next
 * re-render). Writing a CSS variable updates the transient container's `top`
 * without ever re-rendering it.
 *
 * ResizeObserver is useless for the measurement: in stacked mode every toast
 * is position:absolute + transformed, so the container's content height stays
 * ~0 and contentRect ignores transforms. Instead:
 * - collapsed: measure the toast nodes' bounding boxes (transform-aware) after
 *   the stack transition has settled
 * - expanded (hovered): sum the toasts' layout heights (offsetHeight is
 *   transform-independent), available immediately on mouseenter — no need to
 *   wait for the expand transition
 */
export function useSystemAnnouncementsStackHeight(): void {
    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout> | undefined;
        let hovered = false;

        const applyOffset = (stackHeight: number) => {
            document.documentElement.style.setProperty(
                SYSTEM_ANNOUNCEMENTS_OFFSET_VAR,
                stackHeight > 0 ? `${stackHeight + STACK_GAP}px` : '0px'
            );
        };

        const measure = () => {
            const section = document.getElementById(SYSTEM_ANNOUNCEMENTS_CONTAINER_ID);
            const toasts = section?.querySelectorAll('.Toastify__toast');
            if (!section || !toasts || toasts.length === 0) {
                applyOffset(0);
                return;
            }
            if (hovered) {
                let sum = 0;
                toasts.forEach((node) => {
                    sum += (node as HTMLElement).offsetHeight + STACK_GAP;
                });
                applyOffset(sum - STACK_GAP);
                return;
            }
            const sectionTop = section.getBoundingClientRect().top;
            let maxBottom = sectionTop;
            toasts.forEach((node) => {
                maxBottom = Math.max(maxBottom, node.getBoundingClientRect().bottom);
            });
            applyOffset(Math.max(0, Math.ceil(maxBottom - sectionTop)));
        };

        const scheduleMeasure = () => {
            clearTimeout(timeout);
            timeout = setTimeout(measure, STACK_TRANSITION_MS);
        };

        // The stack expands/collapses on pointer enter/leave of the section
        // (react-toastify's own trigger element). mouseenter/-leave cover the
        // whole subtree, so hovering any toast counts.
        const section = document.getElementById(SYSTEM_ANNOUNCEMENTS_CONTAINER_ID);
        const onEnter = () => {
            hovered = true;
            clearTimeout(timeout);
            measure();
        };
        const onLeave = () => {
            hovered = false;
            scheduleMeasure();
        };
        section?.addEventListener('mouseenter', onEnter);
        section?.addEventListener('mouseleave', onLeave);

        const unsubscribe = toast.onChange(scheduleMeasure);
        window.addEventListener('resize', scheduleMeasure);
        scheduleMeasure();

        return () => {
            unsubscribe();
            window.removeEventListener('resize', scheduleMeasure);
            section?.removeEventListener('mouseenter', onEnter);
            section?.removeEventListener('mouseleave', onLeave);
            clearTimeout(timeout);
            document.documentElement.style.removeProperty(SYSTEM_ANNOUNCEMENTS_OFFSET_VAR);
        };
    }, []);
}
